import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation } from '../../context/LocationContext';
import { getBrowserTimezone } from '../../utils/timezone';
import { fontSizeFixed } from '../../util/responsiveUtils';

/**
 * Gets a short timezone label like "EST", "PST", "MST" for a given IANA timezone name.
 */
function getTzAbbreviation(ianaTimezone: string): string {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: ianaTimezone,
            timeZoneName: 'short',
        }).formatToParts(new Date());
        return parts.find((p) => p.type === 'timeZoneName')?.value ?? ianaTimezone;
    } catch {
        return ianaTimezone;
    }
}

/**
 * Normalises an IANA timezone to a human-readable city name, e.g. "America/Toronto" → "Toronto".
 */
function tzDisplayName(ianaTimezone: string): string {
    return ianaTimezone.split('/').pop()?.replace(/_/g, ' ') ?? ianaTimezone;
}

/**
 * Shown when the user's selected city timezone differs from the browser/OS timezone.
 * Lets the user know that all displayed dates and times are in their selected city's timezone.
 * Dismissed state is stored in sessionStorage (keyed by city+tz so it re-appears on city change).
 */
export const TimezoneInfoBanner: React.FC = () => {
    const { city, timezone, isInitialized } = useLocation();
    const [dismissed, setDismissed] = useState(false);

    const browserTz = getBrowserTimezone();
    const cityTz = timezone;

    // Session-storage key encodes both the city and its timezone so the banner re-appears if the
    // user selects a different city, or the mapping changes.
    const dismissKey = city && cityTz ? `tzBannerDismissed:${city}:${cityTz}` : null;

    useEffect(() => {
        if (!dismissKey) {
            setDismissed(false);
            return;
        }
        setDismissed(sessionStorage.getItem(dismissKey) === '1');
    }, [dismissKey]);

    // Don't show until LocationContext has loaded from localStorage
    if (!isInitialized) return null;

    // Only show when there is an active city selection whose timezone differs from the browser
    if (!city || !cityTz || cityTz === browserTz || dismissed) return null;

    const cityTzAbbr = getTzAbbreviation(cityTz);
    const browserTzAbbr = getTzAbbreviation(browserTz);
    const browserTzLabel = tzDisplayName(browserTz);

    const handleDismiss = () => {
        if (dismissKey) sessionStorage.setItem(dismissKey, '1');
        setDismissed(true);
    };

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                px: 2,
                py: 0.75,
                backgroundColor: 'info.main',
                color: 'info.contrastText',
            }}
        >
            <AccessTimeIcon sx={{ fontSize: fontSizeFixed.rem09, flexShrink: 0, opacity: 0.9 }} />
            <Typography
                variant="caption"
                sx={{ fontWeight: 500, lineHeight: 1.4, textAlign: 'center' }}
            >
                Showing dates &amp; times in{' '}
                <strong>
                    {city} time ({cityTzAbbr})
                </strong>{' '}
                based on your selected city.{' '}
                <span style={{ opacity: 0.85 }}>
                    Your device is set to {browserTzLabel} ({browserTzAbbr}).
                </span>
            </Typography>
            <IconButton
                size="small"
                onClick={handleDismiss}
                aria-label="Dismiss timezone notice"
                sx={{ color: 'inherit', opacity: 0.8, ml: 0.5, p: 0.25, '&:hover': { opacity: 1 } }}
            >
                <CloseIcon sx={{ fontSize: fontSizeFixed.rem09 }} />
            </IconButton>
        </Box>
    );
};

export default TimezoneInfoBanner;
